import { FC, HTMLAttributes, useRef, useState, useEffect, CSSProperties } from 'react';
import { IProductContext, useProductContext } from './useProductContext';
import cn from 'classnames';
import Image from '@base/lib/ThemeImage';
import Link from '@base/lib/ThemeLink';
import { useSize } from 'ahooks';
import { toRecord } from 'base/utils/data';
import { get375CssValue } from 'base/utils/dom-size';
import { isSoldOut } from 'mod/product-list/utils';
import { IListProduct } from 'mod/product-list/interface';

export type IProductOptionsProps = HTMLAttributes<HTMLDivElement>;
export type IThumbsProps = {
    products: IListProduct[];
    gutter: string;
    isMultiRows: boolean;
};

const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';

function getGutter(options: IProductContext['options'], optionType: IProductContext['optionType']) {
    const config = toRecord(options);
    const thumbSpace = get375CssValue(config.space || 3);
    const textSpace = get375CssValue(config.textSpace || 8);
    return optionType === 'image' ? thumbSpace : textSpace;
}

export const Thumbs: FC<IThumbsProps> = props => {
    const { products, gutter, isMultiRows } = props;
    const {
        product,
        onChangeProduct,
        options,
        optionType,
    } = useProductContext();

    const config = toRecord(options);

    const thumbSizeNum = config.size || 40;
    const textLineHeight = get375CssValue(config.textHeight || 24);
    const textSideSpace = get375CssValue(config.textSideSpace || 8);

    return (
        <>
            {products.map((prod, index) => optionType === 'text' ? (
                <div
                    key={index}
                    style={{
                        marginLeft: gutter,
                        marginTop: isMultiRows ? gutter : void 0,
                        lineHeight: textLineHeight,
                        padding: `0 ${textSideSpace}`
                    }}
                    className={cn(
                        'g-option-text',
                        { 'g-active': prod.variantId === product.variantId },
                        { 'g-sold-out': isSoldOut(prod) },
                    )}
                    onClick={() => {
                        onChangeProduct(prod);
                        config.onChange?.(prod);
                    }}
                >
                    {prod.optionText}
                </div>
            ) : (
                <Image
                    key={prod.variantId}
                    width={thumbSizeNum}
                    src={prod.pic.url}
                    alt={prod.pic.altText || 'option item'}
                    fixed
                    className={cn(
                        'g-thumb',
                        { 'g-active': prod.variantId === product.variantId },
                        { 'g-sold-out': isSoldOut(prod) },
                    )}
                    style={{
                        marginLeft: gutter,
                        marginTop: isMultiRows ? gutter : void 0,
                    }}
                    onClick={() => {
                        onChangeProduct(prod);
                        config.onChange?.(prod);
                    }}
                />
            ))}
        </>
    )
}

export const MainAll: FC<IProductOptionsProps> = props => {
    const {
        products,
        options,
        optionType,
    } = useProductContext();

    const { className } = props;

    const gutter = getGutter(options, optionType);

    return (
        <div className={cn(className, 'g-options g-options-all')}>
            <div
                className={'g-thumbs-all'}
                style={{
                    marginLeft: `-${gutter}`,
                    marginTop: `-${gutter}`
                }}>

            </div>
            <Thumbs products={products} gutter={gutter} isMultiRows/>
        </div>
    )
}

export const Main: FC<IProductOptionsProps> = props => {
    const {
        product,
        products: contextProducts,
        num,
        options = {
            maxDisplayNum: 3
        },
        link,
        optionType
    } = useProductContext();

    const { className } = props;
    const thumbsRef = useRef<HTMLDivElement>(null);
    const [moreNum, setMoreNum] = useState<number>(0);

    const config = toRecord(options);
    /*const products = typeof config?.maxDisplayNum === 'number' && config.maxDisplayNum < contextProducts.length
        ? contextProducts.splice(0, config.maxDisplayNum)
        : contextProducts;*/
    const products = [...contextProducts].splice(0, config.maxDisplayNum)

    const thumbSizeNum = config.size || 40;
    const thumbSize = get375CssValue(thumbSizeNum);
    const moreStyle: CSSProperties = {
        minWidth: thumbSize,
        lineHeight: thumbSize,
    }

    const gutter = getGutter(options, optionType);
    const thumbsSize = useSize(thumbsRef);

    const calcMoreNum = (thumbsW: number) => {
        const thumbEls = thumbsRef.current!.children;
        if (thumbEls.length === 0) {
            return;
        }
        const firstThumb = thumbEls[0] as HTMLDivElement;
        const secondThumb = thumbEls[1] as HTMLDivElement;
        const thumbW = secondThumb
            ? secondThumb.getBoundingClientRect().left - firstThumb.getBoundingClientRect().left
            : firstThumb.offsetWidth;

        const visibleThumbsLen = Math.min(products.length, num, Math.max(1, Math.floor(thumbsW / thumbW)));
        setMoreNum(num - visibleThumbsLen);
    };

    useEffect(() => {
        const thumbsW = thumbsSize?.width;
        if (thumbsW && options !== false && num > 1) {
            calcMoreNum(thumbsW);
        }
    }, [thumbsSize]);

    return (
        <div className={cn(className, 'g-options g-options-part')}>
            <div
                ref={thumbsRef}
                className={'g-thumbs'}
                style={{
                    height: thumbSize,
                    marginLeft: `-${gutter}`,
                }}
            >
                <Thumbs products={products} gutter={gutter} isMultiRows={false}/>
            </div>
            {moreNum > 0 && (
                link === false ? (
                    <div className={'g-more g-more-text'} style={moreStyle}>+ {moreNum}</div>
                ) : (
                    <Link href={product.url}>
                        <a className={'g-more g-more-link js-track-link'} style={moreStyle}>+ {moreNum}</a>
                    </Link>
                )
            )}
        </div>
    )
}

export const ProductOptions: FC<IProductOptionsProps> = props => {
    const { num, options } = useProductContext();
    const config = toRecord(options);

    if (options === false || num <= 1) {
        return null;
    }
    if (config?.showAll === true) {
        return <MainAll {...props}/>
    }
    return <Main {...props}/>
};